import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from "@mui/material";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useSelector } from "../../common/hooks/useSelector";
import SpecsRevisionService from "../../service/SpecsRevisionService";
import { SpecsInputField } from "./SpecsEditorFields/index";
import { upsertProductSnapshotIntoCatalog } from "./specsModel/catalog";
import type { SpecsModelSnapshot } from "./specsModel/types";
import { useSpecsEditorContextValue } from "./useSpecsEditor";

export type SpecsDuplicateProductDialogApi = {
  duplicateProductDialogOpen: boolean;
  openDuplicateProductDialog: () => void;
  closeDuplicateProductDialog: () => void;
};

export function useSpecsDuplicateProductDialogApi(): SpecsDuplicateProductDialogApi {
  const [duplicateProductDialogOpen, setDuplicateProductDialogOpen] =
    React.useState(false);

  const openDuplicateProductDialog = React.useCallback(() => {
    setDuplicateProductDialogOpen(true);
  }, []);

  const closeDuplicateProductDialog = React.useCallback(() => {
    setDuplicateProductDialogOpen(false);
  }, []);

  return React.useMemo(
    () => ({
      duplicateProductDialogOpen,
      openDuplicateProductDialog,
      closeDuplicateProductDialog,
    }),
    [
      closeDuplicateProductDialog,
      duplicateProductDialogOpen,
      openDuplicateProductDialog,
    ]
  );
}

export default function SpecsDuplicateProductDialog() {
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.userInfo) as any;
  const { editor, importSpecs, modals } = useSpecsEditorContextValue();
  const [duplicateProductName, setDuplicateProductName] = React.useState("");
  const [duplicateProductDisplayName, setDuplicateProductDisplayName] =
    React.useState("");
  const [duplicateProductVersion, setDuplicateProductVersion] =
    React.useState("");

  React.useEffect(() => {
    if (!modals.duplicateProductDialogOpen) {
      return;
    }
    setDuplicateProductName(
      editor.identity.productName
        ? `${editor.identity.productName}Copy`
        : "newProduct"
    );
    let nextDuplicateProductDisplayName = "New Product";
    if (editor.identity.productName) {
      nextDuplicateProductDisplayName = `${editor.identity.productName} Copy`;
    }
    if (editor.identity.productDisplayName) {
      nextDuplicateProductDisplayName = `${editor.identity.productDisplayName} Copy`;
    }
    setDuplicateProductDisplayName(nextDuplicateProductDisplayName);
    setDuplicateProductVersion(editor.identity.productVersion || "1.0");
  }, [
    editor.identity.productDisplayName,
    editor.identity.productName,
    editor.identity.productVersion,
    modals.duplicateProductDialogOpen,
  ]);

  const handleSubmitDuplicateProduct = async () => {
    const nextProductName = duplicateProductName.trim();
    const nextProductDisplayName =
      duplicateProductDisplayName.trim() || nextProductName;
    const nextProductVersion = duplicateProductVersion.trim() || "1.0";
    if (!nextProductName) {
      toast.error("New product name is required");
      return;
    }

    // Resolve family from catalog for local state only (the product itself is standalone)
    const family = importSpecs?.families?.find((candidate: any) =>
      candidate?.versions?.some((version: any) =>
        version?.products?.some(
          (product: any) => product.name === editor.identity.productName
        )
      )
    );

    let productSnapshot: SpecsModelSnapshot;
    try {
      if (!editor.specsModel) {
        throw new Error(
          "Unable to duplicate product without a live specs model"
        );
      }
      productSnapshot = editor.specsModel.duplicate({
        sourceVersionName: editor.identity.productVersion,
        productName: nextProductName,
        productDisplayName: nextProductDisplayName,
        versionName: nextProductVersion,
      });
      await SpecsRevisionService.saveSpecRevision({
        specType: "product",
        specName: nextProductName,
        snapshot: productSnapshot,
        createdBy: currentUser?.email || currentUser?.id || "unknown",
        changeNote: `Duplicated from ${editor.identity.productName} ${editor.identity.productVersion}`,
      });
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to persist duplicated product"
      );
      return;
    }

    editor.updateIdentity({
      productName: nextProductName,
      productDisplayName: nextProductDisplayName,
      productVersion: nextProductVersion,
    });
    editor.setImportSpecs((currentSpecs: any) =>
      upsertProductSnapshotIntoCatalog(
        currentSpecs,
        productSnapshot,
        family?.name
      )
    );
    editor.setImportSelectedProduct(nextProductName);
    editor.setImportSelectedVersion(nextProductVersion);
    modals.closeDuplicateProductDialog();
    navigate(`/specs/${nextProductName}/${nextProductVersion}`, {
      replace: false,
    });
    toast.success(
      `Duplicated session to ${nextProductName} ${nextProductVersion}`
    );
  };

  return (
    <Dialog
      open={modals.duplicateProductDialogOpen}
      onClose={modals.closeDuplicateProductDialog}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>Duplicate Product</DialogTitle>
      <DialogContent sx={{ pt: "8px !important" }}>
        <Stack spacing={1.5}>
          <SpecsInputField
            autoFocus
            label="New Product Name"
            value={duplicateProductName}
            onChange={(event) => setDuplicateProductName(event.target.value)}
            inputProps={{
              "data-test": "specsEditor.duplicateProduct.name",
            }}
          />
          <SpecsInputField
            label="Display name"
            value={duplicateProductDisplayName}
            onChange={(event) =>
              setDuplicateProductDisplayName(event.target.value)
            }
            inputProps={{
              "data-test": "specsEditor.duplicateProduct.displayName",
            }}
          />
          <SpecsInputField
            label="Version"
            value={duplicateProductVersion}
            onChange={(event) => setDuplicateProductVersion(event.target.value)}
            inputProps={{
              "data-test": "specsEditor.duplicateProduct.version",
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                handleSubmitDuplicateProduct();
              }
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={modals.closeDuplicateProductDialog} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          size="small"
          disabled={!duplicateProductName.trim()}
          onClick={handleSubmitDuplicateProduct}
          data-test="specsEditor.duplicateProduct.submit"
        >
          Duplicate
        </Button>
      </DialogActions>
    </Dialog>
  );
}
